<template>
  <ClienteSideDrawer
    :open="open && !!usuario"
    :title="t('superAdmin.billing.drawerTitle')"
    :subtitle="drawerSubtitle"
    wide
    panel-class="sa-billing-panel"
    body-class="cliente-side-drawer-body--flush"
    @close="emit('close')"
  >
    <template #icon>
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    </template>

    <div v-if="usuario" class="cliente-side-drawer-pane cliente-side-drawer-pane--financeiro p-0">
      <div v-if="loading" class="cliente-financeiro-drawer__loading">
        <p>{{ t('common.loading') }}</p>
      </div>
      <div v-else class="cliente-financeiro-drawer">
        <div class="cliente-financeiro-drawer__toolbar">
          <p class="cliente-financeiro-drawer__hint">{{ t('superAdmin.billing.drawerHint') }}</p>
          <button type="button" class="cliente-financeiro-drawer__add" @click="openForm()">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            {{ t('superAdmin.billing.addCobranca') }}
          </button>
        </div>

        <div v-if="stats.total" class="cliente-financeiro-drawer__stats">
          <button
            type="button"
            class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--pending"
            :class="statClass('pending')"
            :aria-pressed="filters.has('pending') && filters.size < 3"
            @click="toggleFilter('pending')"
          >
            <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="cliente-financeiro-drawer__stat-body">
              <span class="cliente-financeiro-drawer__stat-label">{{ t('superAdmin.billing.statPending') }}</span>
              <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(stats.pendingTotal, locale) }}</strong>
              <span v-if="stats.openCount" class="cliente-financeiro-drawer__stat-meta">
                {{ t('superAdmin.billing.statOpenCount', { count: stats.openCount }) }}
              </span>
            </div>
          </button>
          <button
            type="button"
            class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--paid"
            :class="statClass('paid')"
            :aria-pressed="filters.has('paid') && filters.size < 3"
            @click="toggleFilter('paid')"
          >
            <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="cliente-financeiro-drawer__stat-body">
              <span class="cliente-financeiro-drawer__stat-label">{{ t('superAdmin.billing.statPaid') }}</span>
              <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(stats.paidTotal, locale) }}</strong>
            </div>
          </button>
          <button
            v-if="stats.overdueCount"
            type="button"
            class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--alert"
            :class="statClass('overdue')"
            :aria-pressed="filters.has('overdue') && filters.size < 3"
            @click="toggleFilter('overdue')"
          >
            <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="cliente-financeiro-drawer__stat-body">
              <span class="cliente-financeiro-drawer__stat-label">{{ t('superAdmin.billing.statOverdue') }}</span>
              <strong class="cliente-financeiro-drawer__stat-value">{{ stats.overdueCount }}</strong>
            </div>
          </button>
        </div>

        <div v-if="!cobrancasOrdenadas.length" class="cliente-financeiro-drawer__empty cliente-empty-state">
          <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p>{{ t('superAdmin.billing.empty') }}</p>
          <button type="button" class="cliente-empty-state__cta" @click="openForm()">{{ t('superAdmin.billing.addCobranca') }}</button>
        </div>

        <div v-else class="cliente-financeiro-drawer__list-wrap">
          <div class="cliente-financeiro-drawer__list-head">
            <h3 class="cliente-financeiro-drawer__list-title">{{ t('superAdmin.billing.listTitle') }}</h3>
            <span class="cliente-financeiro-drawer__list-count">{{ cobrancasFiltradas.length }}</span>
          </div>
          <div
            v-if="!cobrancasFiltradas.length"
            class="cliente-financeiro-drawer__filter-empty"
          >
            <p>{{ t('superAdmin.billing.filterEmpty') }}</p>
            <button type="button" class="cliente-financeiro-drawer__filter-reset" @click="resetFilters">
              {{ t('superAdmin.billing.filterShowAll') }}
            </button>
          </div>
          <div v-else class="cliente-financeiro-drawer__list">
            <ClienteRecebivelCard
              v-for="item in cobrancasFiltradas"
              :key="item.id"
              variant="drawer"
              :item="item"
              :locale="dateLocale"
              :status-class="recebivelStatusClass(item)"
              :bar-class="recebivelBarClass(item)"
              :badge-class="recebivelBadgeClass(item)"
              :status-label="recebivelStatusLabel(item)"
              :format-date-only="formatDateOnly"
              :show-delete="true"
              @edit="openForm"
              @toggle-pago="onTogglePago"
              @delete="requestDelete"
            />
          </div>
        </div>
      </div>
    </div>

    <SuperAdminUsuarioCobrancaFormModal
      :open="showForm"
      :initial="editItem"
      :default-valor="defaultValor"
      :default-vencimento="defaultVencimento"
      @close="closeForm"
      @save="onSave"
    />

    <ClienteConfirmModal
      :open="!!deleteTarget"
      :title="t('superAdmin.billing.deleteTitle')"
      :message="t('superAdmin.billing.deleteMessage')"
      :loading="deleting"
      @cancel="deleteTarget = null"
      @confirm="confirmDelete"
    />
  </ClienteSideDrawer>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import { formatMoneyAmount, parseLocalDate } from '@/utils/format'
import { useRecebiveisDisplay } from '@/composables/useRecebiveisDisplay'
import ClienteSideDrawer from '../cliente/ClienteSideDrawer.vue'
import ClienteRecebivelCard from '../cliente/ClienteRecebivelCard.vue'
import ClienteConfirmModal from '../cliente/ClienteConfirmModal.vue'
import SuperAdminUsuarioCobrancaFormModal from './SuperAdminUsuarioCobrancaFormModal.vue'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  open: { type: Boolean, default: false },
  usuario: { type: Object, default: null }
})

const emit = defineEmits(['close', 'updated'])

const { t, locale } = useI18n()
const toast = useToast()
const {
  formatDateOnly,
  recebivelStatusKey,
  recebivelStatusClass,
  recebivelStatusLabel,
  recebivelBadgeClass,
  recebivelBarClass
} = useRecebiveisDisplay()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const drawerSubtitle = computed(() => {
  const nome = props.usuario?.nomeCompleto || ''
  const email = props.usuario?.email || ''
  if (nome && email) return `${nome} · ${email}`
  return nome || email
})

function normalizeCobrancaItem (raw) {
  if (!raw || typeof raw !== 'object') return raw
  const item = { ...raw }
  const venc = parseLocalDate(item.dataVencimento)
  if (venc) item.dataVencimento = venc.toISOString().slice(0, 10)
  const pago = parseLocalDate(item.dataPagamento)
  if (pago) item.dataPagamento = pago.toISOString().slice(0, 10)
  return item
}

const loading = ref(false)
const cobrancas = ref([])
const showForm = ref(false)
const editItem = ref(null)
const deleteTarget = ref(null)
const deleting = ref(false)
const filters = ref(new Set(['pending', 'paid', 'overdue']))

const defaultValor = computed(() => {
  const v = Number(props.usuario?.valorMensalCobranca)
  return v > 0 ? v : null
})

const defaultVencimento = computed(() => {
  const dia = props.usuario?.diaVencimentoCobranca
  if (!dia) return ''
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const last = new Date(y, m + 1, 0).getDate()
  const d = Math.min(dia, last)
  const candidate = new Date(y, m, d)
  const target = candidate < new Date(now.getFullYear(), now.getMonth(), now.getDate())
    ? new Date(y, m + 1, Math.min(dia, new Date(y, m + 2, 0).getDate()))
    : candidate
  return target.toISOString().slice(0, 10)
})

const cobrancasOrdenadas = computed(() =>
  [...cobrancas.value].sort((a, b) => String(a.dataVencimento || '').localeCompare(String(b.dataVencimento || '')))
)

function matchesFilter (item) {
  if (filters.value.size === 3) return true
  const status = recebivelStatusKey(item)
  const isPaid = !!item.pago
  const isOverdue = !isPaid && status === 'overdue'
  const isPending = !isPaid && status !== 'overdue'
  if (filters.value.has('paid') && isPaid) return true
  if (filters.value.has('overdue') && isOverdue) return true
  if (filters.value.has('pending') && isPending) return true
  return false
}

const cobrancasFiltradas = computed(() => cobrancasOrdenadas.value.filter(matchesFilter))

const stats = computed(() => {
  let pendingTotal = 0
  let paidTotal = 0
  let overdueCount = 0
  let openCount = 0
  for (const item of cobrancasOrdenadas.value) {
    const valor = Number(item.valor) || 0
    if (item.pago) paidTotal += valor
    else {
      pendingTotal += valor
      openCount++
      if (recebivelStatusKey(item) === 'overdue') overdueCount++
    }
  }
  return { pendingTotal, paidTotal, overdueCount, openCount, total: cobrancasOrdenadas.value.length }
})

function resetFilters () {
  filters.value = new Set(['pending', 'paid', 'overdue'])
}

function statClass (key) {
  return filters.value.has(key) && filters.value.size < 3
    ? 'cliente-financeiro-drawer__stat--filter-active'
    : 'cliente-financeiro-drawer__stat--filter-inactive'
}

function toggleFilter (key) {
  if (filters.value.size === 3) {
    filters.value = new Set([key])
    return
  }
  const next = new Set(filters.value)
  if (next.has(key) && next.size > 1) next.delete(key)
  else next.add(key)
  if (!next.size) next.add(key)
  filters.value = next
}

async function loadCobrancas () {
  if (!props.usuario?.id) return
  loading.value = true
  try {
    const { data } = await api.get(`/super-admin/usuarios/${props.usuario.id}/cobrancas`)
    cobrancas.value = Array.isArray(data) ? data.map(normalizeCobrancaItem) : []
  } catch (error) {
    toast.error(error?.response?.data?.error || t('superAdmin.billing.loadError'))
    cobrancas.value = []
  } finally {
    loading.value = false
  }
}

function openForm (item = null) {
  editItem.value = item
  showForm.value = true
}

function closeForm () {
  showForm.value = false
  editItem.value = null
}

async function onSave (payload) {
  if (!props.usuario?.id) return
  try {
    if (editItem.value?.id) {
      await api.put(`/super-admin/usuarios/${props.usuario.id}/cobrancas/${editItem.value.id}`, payload)
      toast.success(t('superAdmin.billing.updated'))
    } else {
      const body = { ...payload }
      if (body.parcelas == null && body.recorrenciaSempre) {
        body.recorrenciaSempre = true
      } else if (body.parcelas) {
        body.parcelas = Number(body.parcelas)
      } else {
        delete body.parcelas
        delete body.recorrenciaSempre
      }
      await api.post(`/super-admin/usuarios/${props.usuario.id}/cobrancas`, body)
      toast.success(t('superAdmin.billing.created'))
    }
    closeForm()
    await loadCobrancas()
    emit('updated')
  } catch (error) {
    toast.error(error?.response?.data?.error || t('superAdmin.billing.saveError'))
  }
}

async function onTogglePago (item) {
  if (!props.usuario?.id || !item?.id) return
  try {
    if (item.pago) {
      await api.put(`/super-admin/usuarios/${props.usuario.id}/cobrancas/${item.id}/desmarcar-pago`)
    } else {
      await api.put(`/super-admin/usuarios/${props.usuario.id}/cobrancas/${item.id}/marcar-pago`)
    }
    await loadCobrancas()
    emit('updated')
  } catch (error) {
    toast.error(error?.response?.data?.error || t('superAdmin.billing.toggleError'))
  }
}

function requestDelete (item) {
  deleteTarget.value = item
}

async function confirmDelete () {
  if (!deleteTarget.value || !props.usuario?.id) return
  deleting.value = true
  try {
    await api.delete(`/super-admin/usuarios/${props.usuario.id}/cobrancas/${deleteTarget.value.id}`)
    toast.success(t('superAdmin.billing.deleted'))
    deleteTarget.value = null
    await loadCobrancas()
    emit('updated')
  } catch (error) {
    toast.error(error?.response?.data?.error || t('superAdmin.billing.deleteError'))
  } finally {
    deleting.value = false
  }
}

watch(
  () => [props.open, props.usuario?.id],
  ([isOpen, usuarioId]) => {
    if (isOpen && usuarioId) {
      resetFilters()
      loadCobrancas()
    }
  }
)
</script>
